import { useEffect, useRef, useState } from "react";
import { Check, FileSearch, Loader2, Minus, Plus, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, errorMessage } from "@/lib/api";
import { useLines } from "@/lib/components";

// Phase 24 "What the AI saw": the drawing snippet next to a read line, and a page viewer to compare.
const cache = new Map();

export function useSnipUrl(path) {
  const [url, setUrl] = useState(cache.get(path) || "");
  useEffect(() => {
    if (!path || cache.has(path)) { setUrl(cache.get(path) || ""); return undefined; }
    let live = true;
    api.get("/snips/image", { params: { path }, responseType: "blob" }).then((r) => {
      const u = URL.createObjectURL(r.data);
      cache.set(path, u);
      if (live) setUrl(u);
    }).catch(() => {});
    return () => { live = false; };
  }, [path]);
  return url;
}

const when = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

export function SnipThumb({ row, idx, size = "lg", onOpen }) {
  const s = row.snips?.[0];
  const url = useSnipUrl(s?.crop || s?.page_img);
  if (!s) return null;
  const small = size === "sm";
  return (
    <button data-testid={`quote-item-snip-${small ? "mini-" : ""}${idx}`} onClick={onOpen} title="What the AI saw — open the drawing"
      className={`group relative shrink-0 self-start overflow-hidden rounded-md border bg-white text-left transition-transform hover:scale-[1.02] ${row.checked_at ? "border-emerald-500/70" : "border-slate-700"} ${small ? "h-[60px] w-[84px]" : "w-[260px]"}`}>
      <div className={`grid place-items-center bg-white ${small ? "h-full" : "h-[150px]"}`}>
        {url ? <img src={url} alt={`Drawing p.${s.page}`} className="max-h-full max-w-full object-contain" />
          : (s.crop || s.page_img) ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : <FileSearch className="h-5 w-5 text-slate-400" />}
      </div>
      {!small && (
        <div className="flex items-center justify-between gap-2 bg-slate-900 px-2 py-1 text-[11px] text-slate-300">
          <span data-testid={`quote-item-snip-caption-${idx}`}>{s.crop ? `From drawing · p.${s.page}` : s.how === "approx" ? `Approximate area · open p.${s.page}` : "Location not found — open page"}</span>
          {row.checked_at && <span data-testid={`quote-item-snip-checked-${idx}`} className="flex items-center gap-1 text-emerald-400"><Check className="h-3 w-3" />Checked</span>}
        </div>
      )}
      {small && row.checked_at && <Check className="absolute right-0.5 top-0.5 h-3.5 w-3.5 rounded-full bg-emerald-500 p-0.5 text-white" />}
    </button>
  );
}

function ZoomPage({ snip }) {
  const url = useSnipUrl(snip.page_img);
  const [t, setT] = useState({ s: 1, x: 0, y: 0 });
  const pts = useRef(new Map());
  const last = useRef(null);
  const [nat, setNat] = useState(null);
  const wrap = useRef(null);
  const [cs, setCs] = useState(null);
  useEffect(() => { // the viewer's size, so the whole page fits at reset
    const el = wrap.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([e]) => setCs([e.contentRect.width, e.contentRect.height]));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const fit = nat && cs ? (() => { const w = Math.min(cs[0], (cs[1] * nat[0]) / nat[1]); return [w, (w * nat[1]) / nat[0]]; })() : null;
  const box = snip.box?.length === 4 ? snip.box : null;
  useEffect(() => { // start zoomed on the highlighted area
    if (!box || !fit) return setT({ s: 1, x: 0, y: 0 });
    const s = Math.min(4, Math.max(1, 0.5 / Math.max(box[2] - box[0], box[3] - box[1])));
    setT({ s, x: (0.5 - (box[0] + box[2]) / 2) * (fit[0] / cs[0]) * 100 * s, y: (0.5 - (box[1] + box[3]) / 2) * (fit[1] / cs[1]) * 100 * s });
  }, [snip.page_img, box?.join(","), !!fit]); // eslint-disable-line react-hooks/exhaustive-deps
  const zoom = (f) => setT((o) => { const s = Math.min(8, Math.max(1, o.s * f)); return { s, x: (o.x * s) / o.s, y: (o.y * s) / o.s }; });
  const down = (e) => { e.currentTarget.setPointerCapture(e.pointerId); pts.current.set(e.pointerId, [e.clientX, e.clientY]); last.current = null; };
  const move = (e) => {
    if (!pts.current.has(e.pointerId)) return;
    const el = e.currentTarget.getBoundingClientRect();
    const prev = pts.current.get(e.pointerId);
    pts.current.set(e.pointerId, [e.clientX, e.clientY]);
    if (pts.current.size === 2) {
      const [a, b] = [...pts.current.values()];
      const d = Math.hypot(a[0] - b[0], a[1] - b[1]);
      if (last.current) zoom(d / last.current);
      last.current = d;
      return;
    }
    setT((o) => ({ ...o, x: o.x + ((e.clientX - prev[0]) / el.width) * 100, y: o.y + ((e.clientY - prev[1]) / el.height) * 100 }));
  };
  const up = (e) => { pts.current.delete(e.pointerId); last.current = null; };
  return (
    <div ref={wrap} className="relative h-full min-h-[320px] overflow-hidden rounded-lg bg-slate-100" data-testid="snip-viewer-page"
      onWheel={(e) => zoom(e.deltaY < 0 ? 1.15 : 1 / 1.15)} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
      style={{ touchAction: "none", cursor: "grab" }}>
      {!url && (snip.page_img
        ? <Loader2 className="absolute left-1/2 top-1/2 h-6 w-6 animate-spin text-slate-400" />
        : <p data-testid="snip-viewer-no-page" className="absolute inset-0 grid place-items-center text-sm text-slate-500">Location not found — no page image for this line</p>)}
      {url && (
        <div className="absolute inset-0 grid place-items-center" style={{ transform: `translate(${t.x}%, ${t.y}%) scale(${t.s})`, transformOrigin: "50% 50%", transition: pts.current.size ? "none" : "transform 200ms ease-out" }}>
          {/* fitted to the viewer at the page's own aspect ratio: the whole page shows and the highlight lines up */}
          <div className="relative" style={fit ? { width: fit[0], height: fit[1] } : { height: "100%" }}>
            <img src={url} alt={`Page ${snip.page}`} draggable={false} onLoad={(e) => setNat([e.target.naturalWidth, e.target.naturalHeight])}
              className="block h-full w-full select-none" />
            {box && nat && <PageMark box={box} approx={snip.how === "approx"} />}
          </div>
        </div>
      )}
      <div className="absolute bottom-2 right-2 flex gap-1" onPointerDown={(e) => e.stopPropagation()}>
        {[[Minus, () => zoom(1 / 1.4), "out"], [Plus, () => zoom(1.4), "in"], [RotateCcw, () => setT({ s: 1, x: 0, y: 0 }), "reset"]].map(([I, f, k]) => (
          <button key={k} data-testid={`snip-viewer-zoom-${k}`} onClick={f} aria-label={`Zoom ${k}`}
            className="grid h-11 w-11 place-items-center rounded-full bg-black text-white shadow-lg ring-1 ring-white/40 hover:bg-amber-600"><I className="h-4 w-4" /></button>
        ))}
      </div>
    </div>
  );
}

function PageMark({ box, approx }) {
  // object-contain letterboxes the page; the mark uses an SVG with the same aspect so it lines up.
  return (
    <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 1000 1000" preserveAspectRatio="none" data-testid="snip-viewer-highlight">
      <rect x={box[0] * 1000} y={box[1] * 1000} width={(box[2] - box[0]) * 1000} height={(box[3] - box[1]) * 1000}
        fill={approx ? "none" : "rgba(255,149,0,0.18)"} stroke="#FF6A00" strokeWidth="3" strokeDasharray={approx ? "8 6" : undefined} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

const FIELDS = [["mark", "Mark", "text"], ["width_in", "Width (in)", "number"], ["height_in", "Height (in)", "number"], ["qty", "Qty", "number"]];

export function SnipViewer({ row, idx, open, onClose, onPatch }) {
  const { quoteId, locked, frozen, applyServer, prepare } = useLines();
  const [i, setI] = useState(0);
  const [busy, setBusy] = useState(false);
  const snips = row.snips || [];
  const s = snips[Math.min(i, snips.length - 1)];
  const readOnly = frozen || locked;
  const check = async (checked) => {
    setBusy(true);
    try {
      if (!quoteId) { toast.error("Save the estimate first"); return; }
      await prepare?.();
      const { data } = await api.post(`/quotes/${quoteId}/items/${row.id}/check`, { checked });
      applyServer?.(data);
      if (checked) { toast.success("Marked as checked against the drawing"); onClose(); }
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  if (!s) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`snip-viewer-${idx}`} className="max-w-[min(1200px,96vw)] border-slate-800 bg-slate-950 p-4 text-slate-100">
        <DialogTitle className="flex items-center gap-2 text-base"><FileSearch className="h-4 w-4 text-amber-400" />What the AI saw · line {idx + 1}</DialogTitle>
        <div className="grid gap-4 md:grid-cols-[1fr_300px]" style={{ height: "min(72vh, 760px)" }}>
          <ZoomPage snip={s} />
          <div className="space-y-3 overflow-y-auto">
            {snips.length > 1 && (
              <div className="flex flex-wrap gap-1" data-testid="snip-viewer-readers">
                {snips.map((x, k) => (
                  <button key={k} data-testid={`snip-viewer-reader-${k}`} onClick={() => setI(k)}
                    className={`min-h-[36px] rounded-full px-3 text-xs ${k === i ? "bg-amber-500 text-black" : "bg-slate-800 text-slate-300"}`}>{x.reader || `Reader ${k + 1}`}</button>
                ))}
              </div>
            )}
            <p data-testid="snip-viewer-source" className="text-xs text-slate-400">{row.source_file || "Drawing"} · page {s.page}
              {s.how === "none" ? " · location not found — the whole page is shown" : s.how === "text" ? " · found in the drawing text"
                : s.how === "approx" ? " · the plan reader's approximate area (dashed) — check the page" : " · located by the reader"}</p>
            <div className="grid grid-cols-2 gap-2">
              {FIELDS.map(([k, label, type]) => (
                <label key={k} className="block">
                  <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
                  <input data-testid={`snip-viewer-${k}`} type={type} value={row[k] ?? ""} disabled={readOnly}
                    onChange={(e) => onPatch({ [k]: type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value })}
                    className="h-11 w-full rounded-md border border-slate-800 bg-slate-900 px-2 text-sm disabled:opacity-60" />
                </label>
              ))}
            </div>
            <div className="rounded-md bg-slate-900 px-3 py-2 text-sm" data-testid="snip-viewer-values">
              <p><span className="text-slate-500">Type </span>{row.type_name || row.raw_type || "—"}</p>
              <p><span className="text-slate-500">Configuration </span>{row.layout || row.design_summary || "—"}</p>
              <p><span className="text-slate-500">Read as </span>{row.raw_type || "—"}{row.notes ? ` · ${row.notes}` : ""}</p>
            </div>
            {row.checked_at
              ? <p data-testid="snip-viewer-checked" className="flex items-center gap-1.5 text-sm text-emerald-400"><Check className="h-4 w-4" />Checked by {row.checked_by} · {when(row.checked_at)}</p>
              : null}
            {!frozen && (
              <div className="flex gap-2">
                {!row.checked_at && <button data-testid="snip-viewer-looks-right" disabled={busy} onClick={() => check(true)}
                  className="min-h-[44px] flex-1 rounded-full bg-emerald-500 px-4 text-sm font-semibold text-black disabled:opacity-50">Looks right</button>}
                {row.checked_at && <button data-testid="snip-viewer-uncheck" disabled={busy} onClick={() => check(false)}
                  className="min-h-[44px] flex-1 rounded-full bg-slate-800 px-4 text-sm text-slate-200 disabled:opacity-50">Undo check</button>}
              </div>
            )}
            {frozen && <p data-testid="snip-viewer-readonly" className="text-xs text-slate-500">Approved estimate — read-only</p>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function EstimatePhoto({ row, idx }) {
  const url = useSnipUrl(row.estimate_photo);
  return (
    <figure data-testid={`quote-item-estimate-photo-${idx}`} className="h-[240px] w-[180px] shrink-0 overflow-hidden rounded-md border border-slate-700 bg-white">
      <div className="grid h-[210px] place-items-center p-2">{url ? <img src={url} alt="From estimate" className="max-h-full max-w-full object-contain" /> : <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}</div>
      <figcaption className="bg-slate-900 px-2 py-1 text-[11px] text-slate-300">From estimate</figcaption>
    </figure>
  );
}

export function SnipBanner({ rows }) {
  const read = rows.filter((r) => r.snips?.length);
  if (!read.length) return null;
  const n = read.filter((r) => r.checked_at).length;
  return (
    <div data-testid="snip-check-banner" className={`flex items-center gap-2 rounded-[12px] px-4 py-3 text-[15px] ${n === read.length ? "bg-emerald-500/10 text-emerald-300" : "bg-sky-500/10 text-sky-200"}`}>
      <FileSearch className="h-4 w-4" /> {n} of {read.length} line{read.length === 1 ? "" : "s"} checked against the drawing
    </div>
  );
}
